'use client'

import * as Sentry from '@sentry/nextjs'
import { useEffect } from 'react'

// Last-resort error boundary. Next renders this INSTEAD of the root layout when
// an error escapes the root layout itself or a React render throws — the one
// class of error that no other Sentry hook sees: `onRequestError` covers server
// requests, and the browser SDK's global handlers cover thrown/rejected
// exceptions, but a render-phase failure inside a Server Component boundary
// lands here and nowhere else. Without this file those errors were invisible.
//
// It replaces <html>/<body>, so none of the root layout's fonts or tokens are
// mounted — the markup below is deliberately self-contained and inline-styled.
export default function GlobalError({ error }: { error: Error & { digest?: string } }) {
  useEffect(() => {
    Sentry.captureException(error)
  }, [error])

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 14,
          padding: '0 24px',
          textAlign: 'center',
          background: '#faf9f7',
          color: '#1d1d1a',
          fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
        }}
      >
        <h1 style={{ font: '500 22px/1.2 inherit', margin: 0, letterSpacing: '-0.02em' }}>
          Something went wrong
        </h1>
        <p style={{ margin: 0, fontSize: 15, opacity: 0.7, maxWidth: 420, lineHeight: 1.55 }}>
          The error has been reported and we&rsquo;re looking at it. Reloading usually clears it.
        </p>
        {/* The digest is what ties this screen to the captured event — worth
            showing so a support message can name it. */}
        {error.digest ? (
          <code style={{ fontSize: 12, opacity: 0.45, fontFamily: 'ui-monospace, monospace' }}>
            {error.digest}
          </code>
        ) : null}
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            marginTop: 8,
            font: '500 14px/1 inherit',
            color: '#faf9f7',
            background: '#1d1d1a',
            border: 'none',
            borderRadius: 2,
            padding: '12px 20px',
            cursor: 'pointer',
          }}
        >
          Reload
        </button>
      </body>
    </html>
  )
}
